import { Injectable, Injector } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ConfigurationService } from '../../services/configuration.service.ts/configuration.service';
import {
  StartFlowResultOutputDto,
  TaskResponseResult,
} from '../remote-clients/remote-client';
import { TennantResolverService } from './tennant-resolver.service';

type QueryParams = { [key: string]: string | number | null };

@Injectable()
export class RedirectService {
  protected readonly isGuest: boolean = false;
  protected readonly _route: ActivatedRoute;
  private readonly _config: ConfigurationService;
  private readonly _tenantResolver: TennantResolverService;

  private get tenantId(): string {
    return this._tenantResolver.resolve(this._route);
  }

  constructor(
    injector: Injector,
    config: ConfigurationService,
    tenantResolver: TennantResolverService
  ) {
    this._config = config;
    this._route = injector.get(ActivatedRoute);
    this.isGuest = this._route.snapshot.data['isGuest'];
    this._tenantResolver = tenantResolver;
  }

  public redirectToRespondendTask(
    result: TaskResponseResult,
    versionId: string,
    responseValue: string,
    taskSubject: string
  ) {
    if (this.isGuest) {
      this.redirectToGuestTaskResponded(
        result,
        versionId,
        responseValue,
        taskSubject
      );
      return;
    }

    const baseUrl = this._config.get('DisplayTaskRespondedRoute') as string;
    if (!baseUrl) {
      return;
    }

    const model: DisplayTaskRespondedModel = {
      flowId: result.flowId!,
      confirmationMessage: result.confirmationMessageToUser!,
      versionId: versionId,
      tenantId: this.tenantId,
      selectedOption: responseValue,
      subject: taskSubject,
      useEmptyLayout: false,
    };

    this.postRedirectToUrl(baseUrl, model);
  }

  private redirectToGuestTaskResponded(
    result: TaskResponseResult,
    versionId: string,
    responseValue: string,
    taskSubject: string
  ) {
    const baseUrl = this._config.get(
      'DisplayGuestTaskRespondedRoute'
    ) as string;
    if (!baseUrl) {
      return;
    }

    const model: DisplayTaskRespondedModel = {
      flowId: result.flowId!,
      confirmationMessage: result.confirmationMessageToUser!,
      versionId: versionId,
      tenantId: this.tenantId,
      selectedOption: responseValue,
      subject: taskSubject,
      useEmptyLayout: false,
    };

    this.postRedirectToUrl(baseUrl, model);
  }

  public redirectToFlowStarted(result: StartFlowResultOutputDto) {
    if (this.isGuest) {
      this.redirectToGuestFlowStarted(result);
      return;
    }

    const baseUrl = this._config.get('DisplayFlowStartedRoute') as string;
    if (!baseUrl) {
      return;
    }

    const model: DisplayFlowStartedModel = {
      flowId: result.flowId!,
      confirmationMessage: result.confirmationMessageToUser!,
      flowCorrelativeId: result.flowCorrelativeId!,
      versionId: result.versionId!,
      useEmptyLayout: false,
      tenantId: this.tenantId,
    };

    this.postRedirectToUrl(baseUrl, model);
  }

  private redirectToGuestFlowStarted(result: StartFlowResultOutputDto) {
    const baseUrl = this._config.get('DisplayGuestFlowStartedRoute') as string;
    if (!baseUrl) {
      return;
    }

    const model: DisplayFlowStartedModel = {
      flowId: result.flowId!,
      confirmationMessage: result.confirmationMessageToUser!,
      flowCorrelativeId: result.flowCorrelativeId!,
      versionId: result.versionId!,
      useEmptyLayout: false,
      tenantId: this.tenantId,
    };

    this.postRedirectToUrl(baseUrl, model);
  }

  public redirectToDownloadFlowAttachment(
    flowId: string,
    taskId: string,
    taskToId: string,
    attachId: string,
    version: number,
    tenantId: string,
    token: string,
    email: string
  ) {
    const apiBase = (this._config.get<string>('apiBaseUrl') || '').replace(
      /\/$/,
      ''
    );
    const baseUrl = this.isGuest
      ? `${apiBase}/attachments/guest`
      : `${apiBase}/attachments`;

    const queryParams: QueryParams = this.isGuest
      ? {
        flowId: flowId,
        flowStepId: taskId,
        flowStepToId: taskToId,
        attachId: attachId,
        versionId: version,
        tenantId: tenantId,
        token: token,
        email: email,
      }
      : {
        flowId: flowId,
        attachId: attachId,
        versionId: version,
        tenantId: tenantId,
      };
    this.requestOpenInNewTab(baseUrl, queryParams);
  }

  public redirectToViewFlow(flowId: string): void {
    if (this.isGuest) return;

    const routeTemplate = this._config.get<string>('ViewFlowRoute');
    if (!routeTemplate) return;

    const [baseRoute, queryString] = routeTemplate.split('?');
    const params = new URLSearchParams(queryString ?? '');
    params.set('flowId', flowId);
    const url = `${baseRoute}?${params.toString()}`;
    this.requestRedirect(url);
  }

  private postRedirectToUrl(url: string, model: any): void {
    window.parent.postMessage(
      { name: 'redirectWithPost', args: { url, model } },
      '*'
    );
  }

  private requestOpenInNewTab(baseRoute: string, params: QueryParams): void {
    window.parent.postMessage(
      { name: 'openInNewTab', args: { baseRoute: baseRoute, params: params } },
      '*'
    );
  }

  private requestRedirect(url: string): void {
    window.parent.postMessage(
      { name: 'redirect', args: { url } },
      '*'
    );
  }
}

interface DisplayFlowStartedModel {
  flowId: string;
  confirmationMessage: string;
  flowCorrelativeId: number;
  versionId: string;
  useEmptyLayout: boolean;
  tenantId: string;
}

interface DisplayTaskRespondedModel {
  flowId: string;
  versionId: string;
  tenantId: string;
  confirmationMessage: string;
  selectedOption: string;
  subject: string;
  useEmptyLayout: boolean;
}
